import { FAMILY_LIMITS } from "@pilotfish/core";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppRequestError } from "../api/client";
import {
  confirmRosterWrite,
  type DisbandEnvelope,
  fetchRoster,
  type PendingInvitation,
  proposeDisband,
  proposeInvite,
  proposeLeave,
  proposeRemove,
  proposeRevoke,
  proposeTransfer,
  type RosterMember,
  type Roster as RosterRecord,
  type RosterWriteEnvelope,
  type RosterWriteTail,
} from "../api/roster";
import { Confirm, type ConfirmGate } from "../components/Confirm";
import { InviteForm } from "../components/InviteForm";
import { Screen } from "../components/Screen";
import { ErrorState, Loading } from "../components/states";
import { usePanel } from "../panelContext";
import { PLAN_SECTION } from "../sections";
import { sentenceCase, spell } from "../words";

/**
 * **Who's here** — the roster screen (issue #303, ADR 0019, ADR 0021 decision 5).
 *
 * `invite_member` opened a one-way door: until #115 a family could be joined and never
 * un-joined, and until this screen the five tools that close it could only be reached by
 * saying the right thing to an assistant. ADR 0019 decision 1 names waiting for an app layer
 * as a trap — this is the app layer arriving.
 *
 * ## The paraphrase problem is answered by there being no paraphrase
 *
 * The issue's own worry is that *"remove my husband" is a terrible thing to get wrong from a
 * paraphrase*. Here a member is chosen from a list, so the `memberId` is the server's own and
 * never a name resolved by a model — and the confirm still carries no body, so nothing between
 * the proposal and the apply can change who the write is about.
 *
 * ## Nothing here is a second way to write, and nothing here is an authorization rule
 *
 * Every one of the five is #298's route onto the tool itself: the same plain function, the
 * same tier, the same `PROPOSAL#`, the same permanent `ACTION#` (#109), and `writtenVia:
 * "web"` because the session verifier said so. This component decides **when to ask the
 * server** and **what to render**, and decides nothing about what a write means or who may
 * make it — `canAdminister` arrives from the server, which asks `roleHasCapability` (#107), and
 * a control that is offered wrongly still meets the server's refusal.
 *
 * ## Two lists, because an invitation is not a person
 *
 * `show_roster`'s decision, and it holds harder on a screen: a household shown *"four of
 * you"* when two never replied believes something untrue about itself. The counts, the blurb
 * and the seat allowance are all computed from the members alone.
 */
export function Roster({ familyId }: { familyId: string }) {
  const { api, families, reload: reloadMemberships } = usePanel();
  const familyName = families.find((family) => family.familyId === familyId)?.name ?? null;

  const [load, setLoad] = useState<Load>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  const [inviting, setInviting] = useState(false);
  const [pending, setPending] = useState(false);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [confirming, setConfirming] = useState<Confirming | null>(null);

  // `attempt` is not read in the effect — it IS the trigger, the shell's own idiom
  // (Panel.tsx, Memory.tsx). Every write that applies calls `reload()`.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` is the re-run trigger.
  useEffect(() => {
    let live = true;
    setLoad({ status: "loading" });
    fetchRoster(api, familyId)
      .then((roster) => {
        if (live) setLoad({ status: "ready", roster });
      })
      .catch((error: unknown) => {
        if (live) setLoad({ status: "failed", detail: detailOf(error) });
      });
    return () => {
      live = false;
    };
  }, [api, familyId, attempt]);

  /**
   * Re-read whatever the write may have changed — **one function, so the success branch and
   * the lost-response branch cannot disagree about which**.
   *
   * They did, and that was the bug (PR #330 review): two call sites, one routed through
   * `after` and one not, which is the shape a divergence hides in. There is now nowhere to
   * write the wrong one.
   */
  const settle = useCallback(
    (after: Landing) => {
      if (after === "memberships") reloadMemberships();
      else reload();
    },
    [reload, reloadMemberships],
  );

  /**
   * Send a write, and route its answer — the memory screen's `send`, one screen over.
   *
   * The branch is the *envelope's* rather than the operation's: `proposed` opens the
   * confirmation, `applied` closes whatever was open and re-reads. A refusal shows the
   * server's own sentence, because on this screen more than any other the refusals are
   * answers rather than faults — a full family, a spent invite allowance, the last owner
   * trying to leave — and several of them name the only way out of a state the product
   * otherwise cannot leave (ADR 0019).
   *
   * `after` is what to do once a write lands, and it is a parameter because the two cases are
   * genuinely different: most writes change the roster, while leaving and disbanding change
   * **which families this person has**, which only the shell can answer. Both the applied
   * branch and the ambiguous one go through {@link settle} with it.
   */
  const send = useCallback(
    async (
      run: () => Promise<RosterWriteEnvelope>,
      tail: RosterWriteTail,
      at: RefusalAt,
      after: Landing,
      gate?: ConfirmGate | undefined,
    ): Promise<void> => {
      setPending(true);
      setRefusal(null);
      try {
        const envelope = await run();
        if (envelope.status === "proposed") {
          // A proposal with no id cannot be confirmed, and falling through would close the
          // form and re-read — this screen's whole vocabulary for "that worked", said about a
          // write that did not happen. #298 returns an id with every proposal, so this is
          // unreachable; it is handled because that is the one thing this screen may not do.
          if (envelope.proposalId === null) {
            setRefusal({ detail: GENERIC_WRITE_FAILURE, at });
            return;
          }
          setConfirming({ envelope, tail, proposalId: envelope.proposalId, after, gate });
          return;
        }
        setInviting(false);
        setConfirming(null);
        settle(after);
      } catch (error: unknown) {
        setRefusal({ detail: failureCopy(error), at });
        // A refusal the server answered changed nothing, so re-reading buys nothing. An
        // unanswered one may have committed, and then the screen should show what is actually
        // there rather than leave somebody to guess from a sentence.
        //
        // **Through `after`, exactly as the success branch is** (PR #330 review). This used to
        // re-read the roster whatever the write was — and for the two writes that end the
        // caller's own membership that is a request into a family they may no longer be in.
        // If the confirm committed and only the answer was lost, `GET /roster` comes back
        // `403 not_a_member` or `410 family_deleting`, which puts the screen into its read
        // failure — replacing the honest *"we couldn't tell whether that went through"* with
        // *"nothing you've told Boone is affected"*, about a family that has just been
        // deleted, while the switcher still lists it. The ambiguity is real; that sentence
        // was not.
        if (!wasAnswered(error)) settle(after);
      } finally {
        setPending(false);
      }
    },
    [settle],
  );

  const confirm = () => {
    if (confirming === null) return;
    const { tail, proposalId, after, gate } = confirming;
    void send(
      () => confirmRosterWrite(api, familyId, tail, proposalId),
      tail,
      "dialog",
      after,
      gate,
    );
  };

  if (load.status === "loading") {
    return (
      <Screen title={TITLE}>
        <Loading label="Reading your family's roster…" />
      </Screen>
    );
  }

  if (load.status === "failed") {
    return (
      <Screen title={TITLE}>
        <ErrorState
          title="We couldn't read who's here"
          body={load.detail ?? GENERIC_READ_FAILURE}
        />
      </Screen>
    );
  }

  const { members, invitations } = load.roster;
  const canAdminister = load.roster.canAdminister === true;
  // A write is in flight or a proposal is open: nothing on the page may start a second one.
  // Without this the page behind the scrim stays live, which is how Shift+Tab reached a
  // destructive control and swapped the proposal under the dialog (Confirm.tsx).
  const locked = pending || confirming !== null;
  const seatsLeft = Math.max(0, FAMILY_LIMITS.maxMembers - members.length);

  return (
    <Screen
      title={TITLE}
      blurb={blurb(members.length, invitations)}
      action={
        canAdminister && !inviting ? (
          <button
            className="cta"
            type="button"
            disabled={locked || seatsLeft === 0}
            onClick={() => {
              setRefusal(null);
              setInviting(true);
            }}
          >
            Invite someone
          </button>
        ) : null
      }
    >
      {/* A refusal that belongs to the page rather than to the invite form or the dialog:
          removing, transferring, leaving and cancelling all start with no form open, so their
          refusals arrive with nowhere else to go. `at` is stated by whoever sent the write, so
          a removal refused while the invite form happens to be open no longer lands inside it. */}
      {refusal !== null && refusal.at === "page" ? (
        <div className="roster__refusal" role="alert">
          <p>{refusal.detail}</p>
        </div>
      ) : null}

      {inviting ? (
        <InviteForm
          pending={pending}
          locked={locked}
          error={refusal?.at === "invite" ? refusal.detail : null}
          onSubmit={(email) =>
            void send(
              () => proposeInvite(api, familyId, email),
              "/invite-member",
              "invite",
              "roster",
            )
          }
          onCancel={() => {
            setInviting(false);
            setRefusal(null);
          }}
        />
      ) : null}

      <section className="roster__section">
        <header className="roster__section-head">
          <h2 className="roster__heading">In the family</h2>
          <span className="roster__count">{members.length}</span>
          <span className="roster__spacer" />
          <span className="roster__allowance">{roomFor(seatsLeft)}</span>
        </header>
        <ul className="roster__list">
          {members.map((member) => (
            <MemberRow
              key={member.userId}
              member={member}
              canAdminister={canAdminister}
              locked={locked}
              onLeave={() =>
                void send(() => proposeLeave(api, familyId), "/leave-family", "page", "memberships")
              }
              onMakeOwner={() =>
                void send(
                  () => proposeTransfer(api, familyId, member.userId),
                  "/transfer-ownership",
                  "page",
                  "roster",
                )
              }
              onRemove={() =>
                void send(
                  () => proposeRemove(api, familyId, member.userId),
                  "/remove-member",
                  "page",
                  "roster",
                )
              }
            />
          ))}
        </ul>
      </section>

      <section className="roster__section">
        <header className="roster__section-head">
          <h2 className="roster__heading">Asked, but not here yet</h2>
          <span className="roster__count">{invitations.length}</span>
        </header>
        {invitations.length === 0 ? (
          <p className="roster__empty">Nobody is waiting to join.</p>
        ) : (
          <>
            <ul className="roster__list">
              {invitations.map((invitation) => (
                <InvitationRow
                  key={`${invitation.email}:${invitation.invitedAt}`}
                  invitation={invitation}
                  canAdminister={canAdminister}
                  locked={locked}
                  onRevoke={() =>
                    void send(
                      () => proposeRevoke(api, familyId, invitation.email),
                      "/revoke-invitation",
                      "page",
                      "roster",
                    )
                  }
                />
              ))}
            </ul>
            <p className="roster__note">
              Nobody here can see this family's memory until they accept.
            </p>
          </>
        )}
      </section>

      <div className="roster__plan">
        <span className="roster__plan-body">
          You're on the <strong>Free</strong> plan — up to {spell(FAMILY_LIMITS.maxMembers)} of you.
          {whoLooksAfterIt(members)}
        </span>
        {/* A router `Link`, not an anchor: a bare href would reload the whole bundle and
            re-bootstrap the session to move one route sideways. */}
        <Link className="linklike" to={`/families/${familyId}/${PLAN_SECTION.path}`}>
          Manage the plan
        </Link>
      </div>

      {/*
        **Ending the family**, and it is offered on the same `canAdminister` the four roster
        controls are. `disband_family` has a capability of its own (#228) and is owners-only
        like the other three, so the two answers agree today — and the server is what enforces
        it either way: a caller offered this wrongly meets `NotAFamilyOwnerError`, whose
        sentence is what the refusal above renders.
      */}
      {canAdminister ? (
        <section className="roster__section">
          <header className="roster__section-head">
            <h2 className="roster__heading">Ending the family</h2>
          </header>
          <div className="roster__danger">
            <div className="roster__danger-body">
              <h3 className="roster__danger-title">Delete this family and everything in it</h3>
              <p className="roster__danger-note">
                Everything Boone keeps for {peopleHere(members.length)}, gone for good. Only an
                owner can, and there is no undo.
              </p>
            </div>
            <button
              className="cta cta--danger"
              type="button"
              disabled={locked}
              onClick={() =>
                void send(
                  () => proposeDisband(api, familyId),
                  "/disband-family",
                  "page",
                  "memberships",
                  // The typed-name ceremony. Only offered when the shell knows the family's
                  // name — with no expected value there is nothing to type back, and a gate
                  // that unlocks on the empty string is worse than none, because it looks like
                  // one. See {@link Confirm} for why it is not a security control.
                  familyName === null ? undefined : { label: NAME_GATE, expected: familyName },
                )
              }
            >
              Delete the family
            </button>
          </div>
        </section>
      ) : null}

      {confirming === null ? null : (
        <Confirm
          message={confirming.envelope.message}
          disclosures={doesNotReach(confirming.envelope)}
          gate={confirming.gate}
          pending={pending}
          error={refusal?.at === "dialog" ? refusal.detail : null}
          onConfirm={confirm}
          onCancel={() => {
            setConfirming(null);
            setRefusal(null);
          }}
        />
      )}
    </Screen>
  );
}

/** One person, and what can be done about them. */
function MemberRow({
  member,
  canAdminister,
  locked,
  onLeave,
  onMakeOwner,
  onRemove,
}: {
  member: RosterMember;
  canAdminister: boolean;
  locked: boolean;
  onLeave: () => void;
  onMakeOwner: () => void;
  onRemove: () => void;
}) {
  const isYou = member.isYou === true;
  const isOwner = member.role === "owner";
  return (
    <li className="person">
      <div className="person__body">
        <div className="person__head">
          <h3 className="person__name">{member.name}</h3>
          {isYou ? <span className="pill">you</span> : null}
          {isOwner ? <span className="pill pill--brand">owner</span> : null}
        </div>
        <p className="person__since">{here(member.joinedAt)}</p>
      </div>
      <div className="person__actions">
        {/*
          **Leaving is nobody's to refuse and removing is owners-only**, which is why these two
          are not one control with a branch: there is no `canLeave` capability (ADR 0019), and
          the absent target field on `leave_family` is the authorization boundary. Your own row
          therefore offers exactly one thing, whatever your role.
        */}
        {isYou ? (
          <button className="linklike" type="button" disabled={locked} onClick={onLeave}>
            Leave this family
          </button>
        ) : canAdminister ? (
          <>
            {isOwner ? null : (
              <button className="linklike" type="button" disabled={locked} onClick={onMakeOwner}>
                Make owner
              </button>
            )}
            <button
              className="linklike linklike--destructive"
              type="button"
              disabled={locked}
              onClick={onRemove}
            >
              Remove
            </button>
          </>
        ) : null}
      </div>
    </li>
  );
}

/** One invitation, which is a credential rather than a person. */
function InvitationRow({
  invitation,
  canAdminister,
  locked,
  onRevoke,
}: {
  invitation: PendingInvitation;
  canAdminister: boolean;
  locked: boolean;
  onRevoke: () => void;
}) {
  return (
    <li className="person person--invited">
      <div className="person__body">
        <h3 className="person__name person__name--invited">{invitation.email}</h3>
        <p className="person__since">{invitedLine(invitation)}</p>
      </div>
      <div className="person__actions">
        {canAdminister ? (
          <button
            className="linklike linklike--destructive"
            type="button"
            disabled={locked}
            onClick={onRevoke}
          >
            Cancel the invitation
          </button>
        ) : null}
      </div>
    </li>
  );
}

const TITLE = "Who's here";
const NAME_GATE = "Type the family's name to confirm";
const GENERIC_READ_FAILURE =
  "Something went wrong at our end. Try again in a moment — nothing you've told Boone is affected.";
const GENERIC_WRITE_FAILURE =
  "Something went wrong at our end, and nothing was changed. Try again in a moment.";
/**
 * What to say when we genuinely do not know whether the write landed — `Memory.tsx`'s copy,
 * and it earns its place here more than there: the writes on this screen end people's access.
 */
const AMBIGUOUS_WRITE_FAILURE =
  "We couldn't tell whether that went through. The roster has been re-read below — check it before trying again.";

type Load =
  | { status: "loading" }
  | { status: "failed"; detail: string | null }
  | { status: "ready"; roster: RosterRecord };

/** Where a refusal belongs on the screen — stated by whoever sent the write, never inferred. */
type RefusalAt = "invite" | "dialog" | "page";

interface Refusal {
  readonly detail: string;
  readonly at: RefusalAt;
}

/**
 * What to re-read once a write lands.
 *
 * `roster` is the ordinary case. `memberships` is for the two writes that end this person's
 * relationship with the family — leaving and disbanding — where re-reading the roster would
 * be a request into a family they are no longer in; the shell re-reads what they *are* in and
 * routes accordingly.
 */
type Landing = "roster" | "memberships";

interface Confirming {
  envelope: RosterWriteEnvelope;
  tail: RosterWriteTail;
  proposalId: string;
  after: Landing;
  gate?: ConfirmGate | undefined;
}

/**
 * The server's `doesNotReach` lines, when the envelope carries them.
 *
 * Narrowed at the one place the panel meets it rather than typed into {@link Confirming},
 * because only `disband_family` has them — and a `Confirm` that took a union of envelopes
 * would make the dialog know which tool it is confirming, which is the one thing it must not.
 */
function doesNotReach(envelope: RosterWriteEnvelope): readonly string[] {
  const { doesNotReach: lines } = envelope as Partial<DisbandEnvelope>;
  return Array.isArray(lines) ? lines : [];
}

/**
 * *"Four of you, and one invitation nobody has answered yet."*
 *
 * The two halves are counted separately on purpose — see the component header. A family of
 * one is *"Just you"* rather than *"one of you"*, which is the same number-neutral care
 * `how_to_start` takes: solo is handled, never marketed.
 */
function blurb(members: number, invitations: readonly PendingInvitation[]): string {
  const people = members === 1 ? "just you" : `${spell(members)} of you`;
  const live = invitations.filter((invitation) => !invitation.expired).length;
  const lapsed = invitations.length - live;

  const clauses: string[] = [];
  if (live > 0) {
    clauses.push(
      live === 1
        ? "one invitation nobody has answered yet"
        : `${spell(live)} invitations nobody has answered yet`,
    );
  }
  if (lapsed > 0) {
    // Deliberately not "nobody has answered": these stopped working on their own, and each of
    // their rows says so. Counting them as unanswered is the sentence claiming they are live.
    clauses.push(
      lapsed === 1
        ? "one link that has stopped working"
        : `${spell(lapsed)} links that have stopped working`,
    );
  }
  if (clauses.length === 0) return `${sentenceCase(people)}.`;
  return `${sentenceCase(people)}, and ${clauses.join(", plus ")}.`;
}

/**
 * *"Sam looks after it."* — the canvas's second sentence on the plan row.
 *
 * Only written when there is exactly **one** owner, which is the only case it is true of. With
 * two it would have to pick one, and with none it would name nobody — and the row's job is to
 * tell a family who to ask, not to describe the roster a second time.
 */
function whoLooksAfterIt(members: readonly RosterMember[]): string {
  const owners = members.filter((member) => member.role === "owner");
  const only = owners.length === 1 ? owners[0] : undefined;
  if (only === undefined) return "";
  return only.isYou === true ? " You look after it." : ` ${only.name} looks after it.`;
}

/** *"room for 2 more"* — #251's seat ceiling, said as what it is rather than as a cap. */
function roomFor(seatsLeft: number): string {
  if (seatsLeft === 0) return "no room for anyone else";
  return `room for ${seatsLeft} more`;
}

/** *"all four of you"* / *"you"* — for the sentence about what deleting the family costs. */
function peopleHere(members: number): string {
  return members === 1 ? "you" : `all ${spell(members)} of you`;
}

/**
 * *"here since 11 August"*.
 *
 * The year appears only when it is not this one. A date with no year is how a family reads a
 * recent arrival; a date with the wrong year is how they misread an old one.
 */
function here(joinedAt: string | null | undefined): string {
  const when = day(joinedAt);
  return when === null ? "here since the family started" : `here since ${when}`;
}

function day(iso: string | null | undefined): string | null {
  if (iso === null || iso === undefined) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const thisYear = at.getFullYear() === new Date().getFullYear();
  return at.toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    ...(thisYear ? {} : { year: "numeric" }),
  });
}

/**
 * *"asked by Sam on 18 August — the link stops working in 11 days"*.
 *
 * **An expired invitation says so rather than being hidden.** The server returns lapsed rows
 * on purpose (`show_roster` does too), because whoever is holding that link has no way to know
 * it stopped working — so a family should be able to see it and call it back.
 */
function invitedLine(invitation: PendingInvitation): string {
  const asked = invitation.invitedBy === null ? "asked" : `asked by ${invitation.invitedBy}`;
  const on = day(invitation.invitedAt);
  const when = on === null ? asked : `${asked} on ${on}`;
  return `${when} — ${linkLife(invitation)}`;
}

function linkLife(invitation: PendingInvitation): string {
  if (invitation.expired) return "the link has stopped working";
  const left = Date.parse(invitation.expiresAt) - Date.now();
  if (!Number.isFinite(left)) return "the link is still good";
  const days = Math.ceil(left / 86_400_000);
  if (days <= 1) return "the link stops working today";
  return `the link stops working in ${days} days`;
}

/**
 * The server's sentence for a refusal, or `null` to fall back to a generic one.
 *
 * **`invalid_input` is deliberately dropped**, the memory screen's rule: #298 answers a schema
 * failure with `z.prettifyError`, which names a field's path and is developer-facing by
 * design. A body this panel built failing its schema is our bug rather than something the
 * family did, so the generic line is also the true one.
 */
function detailOf(error: unknown): string | null {
  if (!(error instanceof AppRequestError)) return null;
  return error.code === "invalid_input" ? null : error.detail;
}

function failureCopy(error: unknown): string {
  const detail = detailOf(error);
  if (detail !== null) return detail;
  return wasAnswered(error) ? GENERIC_WRITE_FAILURE : AMBIGUOUS_WRITE_FAILURE;
}

/** Did the server answer at all? `status === 0` is `appFetch`'s "nothing came back". */
function wasAnswered(error: unknown): boolean {
  return error instanceof AppRequestError && error.status > 0;
}
